import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  input,
  output,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { EMPTY, filter, map, shareReplay, switchMap, take, takeWhile, timer } from 'rxjs';

import { ImmomioIconName } from '@resident-nx/shared';
import { formatCountdown } from '@resident-nx/web/util';
import { IconWebComponent } from '../icon/icon.component';

const TICK_INTERVAL_MS = 1000;

/**
 * Published on the document root so viewport-fixed overlays (the side sheet is
 * pinned below the header) can offset themselves by the banner height.
 */
const BANNER_HEIGHT_CSS_VARIABLE = '--rs-impersonation-banner-height';

@Component({
  selector: 'rs-web-impersonation-banner',
  imports: [TranslatePipe, IconWebComponent],
  templateUrl: './impersonation-banner.component.html',
  styleUrl: './impersonation-banner.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImpersonationBannerComponent {
  /** When the impersonation session expires. Omit to hide the countdown. */
  readonly expiresAt = input<Date | null>(null);

  /** Emitted once the countdown hits zero, so the session can be ended. */
  readonly expired = output<void>();

  protected readonly icons = ImmomioIconName;

  readonly #destroyRef = inject(DestroyRef);
  readonly #host = inject<ElementRef<HTMLElement>>(ElementRef);

  // Ticks down to zero and then completes; `takeWhile(..., true)` keeps the
  // final non-positive value so both the display and `expired` see it.
  readonly #remainingMs$ = toObservable(this.expiresAt).pipe(
    switchMap(expiresAt =>
      expiresAt
        ? timer(0, TICK_INTERVAL_MS).pipe(
            map(() => expiresAt.getTime() - Date.now()),
            takeWhile(remainingMs => remainingMs > 0, true)
          )
        : EMPTY
    ),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  protected readonly countdown = toSignal(this.#remainingMs$.pipe(map(formatCountdown)), {
    initialValue: null,
  });

  constructor() {
    // The impersonation token is static — nothing refreshes it — so once it
    // expires every request would fail. End the session the moment it does,
    // which also covers a reload that restores an already-expired token.
    this.#remainingMs$
      .pipe(
        filter(remainingMs => remainingMs <= 0),
        take(1),
        takeUntilDestroyed()
      )
      .subscribe(() => this.expired.emit());

    afterNextRender(() => this.#publishBannerHeight());
  }

  // The banner height changes with the viewport (the text wraps on small
  // screens), so keep the published value in sync instead of hardcoding it.
  #publishBannerHeight(): void {
    const host = this.#host.nativeElement;
    const root = document.documentElement;
    const publish = () =>
      root.style.setProperty(BANNER_HEIGHT_CSS_VARIABLE, `${host.offsetHeight}px`);

    publish();

    const observer = new ResizeObserver(publish);
    observer.observe(host);

    this.#destroyRef.onDestroy(() => {
      observer.disconnect();
      root.style.removeProperty(BANNER_HEIGHT_CSS_VARIABLE);
    });
  }
}
